import * as yup from 'yup';
import { VatRateType } from '../Types/invoicesTypes';
import { isVatExempt, isValidVatType } from '../utils/vatMappings';

/**
 * Enhanced number validation with finite check
 * Prevents NaN, Infinity, and other invalid numeric values
 */
const numberSchema = (fieldName: string) =>
  yup
    .number()
    .typeError(`${fieldName} musi być liczbą`)
    .test('is-finite', `${fieldName} musi być prawidłową liczbą`, function (value) {
      if (value === undefined || value === null) return true;
      if (!Number.isFinite(value)) {
        return this.createError({ message: `${fieldName} musi być prawidłową liczbą` });
      }
      return true;
    })
    .test('not-nan', `${fieldName} nie może być NaN`, function (value) {
      if (value === undefined || value === null) return true;
      if (Number.isNaN(value)) {
        return this.createError({ message: `${fieldName} nie może być NaN` });
      }
      return true;
    });

/**
 * Invoice item validation schema
 *
 * Note: This schema validates UI form state.
 * Fields like `vatRate` and `isVatExempt` are computed before API submission
 * and should NOT be included in the UI form.
 */
type InvoiceItemSchemaOptions = {
  allowZeroQuantity?: boolean;
};

export const createInvoiceItemSchema = ({ allowZeroQuantity = false }: InvoiceItemSchemaOptions = {}) =>
  yup
    .object({
    name: yup
      .string()
      .trim()
      .required('Nazwa nie może być pusta')
      .max(1000, 'Nazwa nie może przekraczać 1000 znaków'),

    description: yup
      .string()
      .nullable()
      .max(500, 'Opis nie może przekraczać 500 znaków')
      .transform((value) => (value === '' ? null : value)),

      quantity: numberSchema('Ilość')
        .required('Podaj ilość')
        .test(
          'quantity-min',
          allowZeroQuantity ? 'Ilość nie może być ujemna' : 'Ilość musi być większa od 0',
          function (value) {
            if (value === undefined || value === null) return true;
            if (allowZeroQuantity ? value >= 0 : value > 0) {
              return true;
            }

            return this.createError({
              message: allowZeroQuantity ? 'Ilość nie może być ujemna' : 'Ilość musi być większa od 0',
            });
          }
        )
        .integer('Ilość musi być liczbą całkowitą')
        .max(999999, 'Ilość nie może przekraczać 999,999'),

    unitNet: numberSchema('Cena jednostkowa netto')
      .required('Podaj cenę jednostkową netto')
      .min(0, 'Cena netto nie może być ujemna')
      .max(9999999.99, 'Cena netto nie może przekraczać 9,999,999.99')
      .test('decimal-places', 'Cena netto może mieć max. 2 miejsca po przecinku', function (value) {
        if (value === undefined || value === null) return true;
        if (!/^\d+(\.\d{1,2})?$/.test(value.toString())) {
          return this.createError({ message: 'Cena netto może mieć max. 2 miejsca po przecinku' });
        }
        return true;
      }),

    unitGross: numberSchema('Cena jednostkowa brutto')
      .nullable()
      .min(0, 'Cena brutto nie może być ujemna')
      .max(9999999.99, 'Cena brutto nie może przekraczać 9,999,999.99')
      .test(
        'decimal-places',
        'Cena brutto może mieć max. 2 miejsca po przecinku',
        function (value) {
          if (value === undefined || value === null) return true;
          if (!/^\d+(\.\d{1,2})?$/.test(value.toString())) {
            return this.createError({
              message: 'Cena brutto może mieć max. 2 miejsca po przecinku',
            });
          }
          return true;
        }
      ),

    // VAT Type is the source of truth for VAT calculations
    // vatRate and isVatExempt are derived from vatType before API submission
    vatType: yup
      .mixed<VatRateType>()
      .required('Wybierz typ stawki VAT')
      .test('is-valid-vat-type', 'Nieprawidłowy typ stawki VAT', function (value) {
        if (!isValidVatType(value)) {
          return this.createError({ message: 'Nieprawidłowy typ stawki VAT' });
        }
        return true;
      }),

    // VAT Exempt Note - conditionally required based on vatType
    vatExemptNote: yup
      .string()
      .nullable()
      .max(500, 'Podstawa prawna zwolnienia nie może przekraczać 500 znaków')
      .transform((value) => {
        if (typeof value !== 'string') {
          return value;
        }

        const trimmedValue = value.trim();
        return trimmedValue === '' ? null : trimmedValue;
      })
      .when('vatType', {
        is: (vatType: VatRateType) => isValidVatType(vatType) && isVatExempt(vatType),
        then: (schema) =>
          schema
            .required('Podaj podstawę prawną zwolnienia z VAT')
            .min(1, 'Podstawa prawna zwolnienia jest wymagana'),
        otherwise: (schema) => schema.nullable(),
      }),
    })
    .required();

export const invoiceItemSchema = createInvoiceItemSchema();
